"use client"

/**
 * Status chips for exam-management rows, and the one place their tones live.
 *
 * These are drawn twice on every hub: once in the Status cell and once inside
 * the filter picker, which lists the same chip beside a checkbox. Keeping both
 * behind one function is what stops the picker from becoming a list of plain
 * words next to a grid of coloured ones.
 *
 * Import the app wrapper, not the DS primitive: the wrapper rebalances the
 * shell's symmetric `px-2.5` into `ps-1.5 pe-2` whenever an icon leads, which
 * is what keeps the glyph off the left edge of the pill.
 */

import {
  StatusBadge,
  type StatusBadgeSemanticSize,
  type StatusBadgeTone,
} from "@/components/ui/status-badge"
import type { Assessment, Submission } from "@/lib/mock/exam-data"

export type AssessmentStatus = Assessment["status"]
/** The empty string is "not submitted yet", which is an absence, not a state. */
export type SubmissionStatus = Exclude<Submission["status"], "">

const ASSESSMENT_STATUS_TONE: Record<AssessmentStatus, StatusBadgeTone> = {
  Draft: "neutral",
  Scheduled: "info",
  Live: "success",
  Evaluating: "warning",
  "Results Published": "success",
}

const ASSESSMENT_STATUS_ICON: Record<AssessmentStatus, string> = {
  Draft: "fa-pen-to-square",
  Scheduled: "fa-calendar-day",
  Live: "fa-signal-stream",
  Evaluating: "fa-arrows-spin",
  "Results Published": "fa-circle-check",
}

const SUBMISSION_STATUS_TONE: Record<SubmissionStatus, StatusBadgeTone> = {
  Graded: "success",
  "In-grading": "warning",
}

const SUBMISSION_STATUS_ICON: Record<SubmissionStatus, string> = {
  Graded: "fa-circle-check",
  "In-grading": "fa-clock",
}

/** Declaration order is picker order, so it reads as a lifecycle. */
export const ASSESSMENT_STATUSES = Object.keys(
  ASSESSMENT_STATUS_TONE,
) as AssessmentStatus[]

export const SUBMISSION_STATUSES = Object.keys(
  SUBMISSION_STATUS_TONE,
) as SubmissionStatus[]

/**
 * Takes a plain `string` rather than the union because some routes build an
 * assessment from a URL slug and cannot prove which status they hold. An
 * unrecognised one still gets a chip, in neutral, instead of an untinted gap.
 */
export function assessmentStatusIcon(status: string): string {
  return ASSESSMENT_STATUS_ICON[status as AssessmentStatus] ?? "fa-circle"
}

/** `size` follows the DS default: `sm` in rows and pickers, `md` on headers. */
export function assessmentStatusChip(
  status: string,
  size: StatusBadgeSemanticSize = "sm",
) {
  return (
    <StatusBadge
      label={status}
      tone={ASSESSMENT_STATUS_TONE[status as AssessmentStatus] ?? "neutral"}
      icon={assessmentStatusIcon(status)}
      size={size}
    />
  )
}

export function submissionStatusChip(
  status: SubmissionStatus,
  size: StatusBadgeSemanticSize = "sm",
) {
  return (
    <StatusBadge
      label={status}
      tone={SUBMISSION_STATUS_TONE[status]}
      icon={SUBMISSION_STATUS_ICON[status]}
      size={size}
    />
  )
}

/** The five states the scoring hub exposes for one student's submission. */
export type SubmissionScoringStatus =
  | "Not started"
  | "Unscored"
  | "Partially scored"
  | "Scored"
  | "Score Released"
  | "Discarded"

/**
 * Green means the work is done, peach means scoring is still owed (unscored or
 * partial), blue means it has left the building, red means the submission has
 * been pulled out of the set.
 */
const SUBMISSION_SCORING_STATUS_TONE: Record<
  SubmissionScoringStatus,
  StatusBadgeTone
> = {
  "Not started": "warning",
  Unscored: "warning",
  "Partially scored": "warning",
  Scored: "success",
  "Score Released": "info",
  Discarded: "danger",
}

export const SUBMISSION_SCORING_STATUSES = Object.keys(
  SUBMISSION_SCORING_STATUS_TONE,
) as SubmissionScoringStatus[]

/**
 * Word plus tone only. Four states this close together read faster as coloured
 * words than as four glyphs the reader has to decode first, and the tone
 * already carries the severity the icon was doubling.
 */
export function submissionScoringStatusChip(
  status: SubmissionScoringStatus,
  size: StatusBadgeSemanticSize = "sm",
) {
  return (
    <StatusBadge
      label={status}
      tone={SUBMISSION_SCORING_STATUS_TONE[status]}
      size={size}
    />
  )
}
